import moment from "moment";
import { ICodeGeneration } from "@edp-components/edp-e-code-generation-modal/code-generation.model";
import { environment } from "@edp-environments/environment";
import { FormatDecimalValue } from "@edp-utils/format-decimal-value.utils";

export interface BenefitsInfo {
    cardTypes: CardType[];
    benefits: ICard[];
}

export interface CardType {
    id: number;
    name: string;
    parentId: number;
    code: string;
}

export interface ICardCampaign {
    codeHtml: string | null;
    description: string | null;
    isHtml: boolean;
    linkUrl: string | null;
    imgDesktopBig: string | null;
    imgDesktopSmall: string | null;
    imgMobile: string | null;
    name: string;
    newTab: boolean;
    title: string | null;
    titleColorDesktop: string | null;
    titleColorMobile: string | null;
}

export interface ICardMechanic {
    id: number;
    label: string;
    shortLabel: string;
    partnerTypeId: number;
}

export interface ICardSteps {
    id: number;
    image: string;
    description: string;
}

export interface ICard {
    filterSubtype: string[];
    partnerTypeAcronym: string;
    isDisabled: boolean;
    icon: string;
    id: number;
    name: string;
    description: string;
    formtypeAcronym?: string;
    image: string;
    imageMobile: string;
    value: string;
    pushPacksEdp: string;
    termsAndConditions: string;
    dateValidationStart?: string;
    dateValidationEnd: string;
    dateValidationEndOriginal?: string;
    participationDate?: string;
    imageId?: string;
    type: Type;
    packIds: number[];
    order: number;
    externalLink?: string;
    campaign: null | ICardCampaign;
    cardTypeId: number;
    cardSubtypeId: number;
    cardSubtype?: string;
    dashboardCoordinateId: number;
    status: string;
    statusName?: string;
    brand: string;
    highlight: boolean;
    points: number;
    codesAvailable?: number;
    generatedCodes?: GeneratedCodes[];
    nextAvailableDate?: string;
    eventName?: string;
    cardSubTypeAcronym?: string;
    categoryId?: number;
    level?: number;
    options: Options[];
    location?: string;
    cardTitle?: string;
    seats?: Seats;
    modalContent?: ICodeGeneration;
    blockedType?: number;
    generatedCode?: GeneratedCodes;
    routeBlocked?: string;
    labelBlocked?: string;
    nextValidityDate?: string;
    appliedRules?: Rules[];
    partners: ICard[];
    showBadge?: boolean;
    eventCode?: string;
    boost?: boolean;
    resultsLink?: string;
    isRegisted?: boolean;
    zNumbers?: number;
    imageReward?: string;
    quizzScript?: string;
    answeredQuizz?: number;
    inMacro?: boolean;
    hasGeneratedCode?: boolean;
    activityTitle?: string;
    activityDescription?: string;
    partnerDescription?: string;
    partnerLink?: string;
    howToUseCode?: string;
    earnedZs?: number;
    btnParticipate: string;
    thumbnailLabel: string;
    thumbnailImage: string;
    partnerAddress: string;
    partnerContact: string;
    winner: string;
    winningAnnouncementDate: string;
    psubtypeAcronym: string;
    mechanicType: ICardMechanic;
    districts: any[];
    partnerStepByStepUsage: ICardSteps[];
    closingDate: string;
    validityCodeDate: string;
    usages: string;
}

export interface Type {
    value: number;
    label: string;
}

export interface Seats {
    available: number;
    capacity: number;
    validity: string;
}

export interface GeneratedCodes {
    id: number;
    code: string;
    type: string;
    validationDate: string;
    assignedDate: string;
}

export interface GeneratedCode {
    id: number;
    code: string;
    validity_date: string;
    reservation_time: string;
}

export interface Options {
    id: number;
    name: string;
    value: string;
}

export interface Rules {
    id: number;
    type: string;
    nextValidityDate: string;
    nextAvailableDate: string;
}

export interface AppliedConditions {
    IS_EDP_USER: boolean;
    HAS_E_INVOICE: boolean;
    BUSINESS_AGREEMENTS: boolean;
}

export class Card {
    constructor(
        public filterSubtype: string[],
        public partnerTypeAcronym: string,
        public isDisabled: boolean,
        public icon: string,
        public btnParticipate: string,
        public location: string,
        public cardTitle: string,
        public id: number,
        public name: string,
        public description: string,
        public formtypeAcronym: string,
        public image: string,
        public imageMobile: string,
        public value: string,
        public dateValidationStart: string,
        public dateValidationEnd: string,
        public dateValidationEndOriginal: string,
        public participationDate: string,
        public imageId: string,
        public type: Type,
        public order: number,
        public externalLink: string,
        public campaign: null | ICardCampaign,
        public cardTypeId: number,
        public cardSubtypeId: number,
        public cardSubtype: string,
        public dashboardCoordinateId: number,
        public status: string,
        public codesAvailable: number,
        public nextAvailableDate: string,
        public level: number,
        public seats: Seats,
        public modalContent: ICodeGeneration,
        public blockedType: number,
        public generatedCode: GeneratedCodes,
        public routeBlocked: string,
        public labelBlocked: string,
        public eventName: string,
        public cardSubTypeAcronym: string,
        public nextValidityDate: string,
        public termsAndConditions: string,
        public appliedRules: Rules[],
        public categoryId: number,
        public partners: ICard[],
        public options: Options[],
        public points: number,
        public generatedCodes: GeneratedCodes[],
        public resultsLink: string,
        public isRegisted: boolean,
        public zNumbers: number,
        public imageReward: string,
        public quizzScript: string,
        public answeredQuizz: number,
        public boost = false,
        public highlight: boolean,
        public activityTitle: string,
        public activityDescription: string,
        public partnerDescription: string,
        public partnerLink: string,
        public howToUseCode: string,
        public earnedZs: number,
        public showBadge = false,
        public inMacro = false,
        public hasGeneratedCode = false,
        public thumbnailLabel = null,
        public thumbnailImage = null,
        public partnerAddress = null,
        public partnerContact = null,
        public winner = null,
        public winningAnnouncementDate = null,
        public psubtypeAcronym = null,
        public mechanicType = null,
        public districts = null,
        public partnerStepByStepUsage = null,
        public closingDate = null,
        public validityCodeDate = null,
        public usages: string,
        public isCampaignPartner = false,
        public isDiscount = false,
        public isExclusiveExperience = false,
        public isGiveAway = false,
        public isIndividualChallenge = false,
        public isCollectiveChallenge = false,
        public isDonative = false,
        public isProductDiscount = false,
        public isContent = false,
        public isQuizz = false,
        public fallback = false
    ) {
        const imageClass = new GetImage();
        this.initCard();
        this.location = this.location || "benefits";
        this.image = imageClass.getImage(this.image, this.cardSubTypeAcronym);
        this.imageMobile = imageClass.getImage(this.imageMobile, this.cardSubTypeAcronym);
        this.termsAndConditions = this.getTermsAndConditions(this.termsAndConditions);
        this.dateValidationStart = this.getMomentDate(this.dateValidationStart);
        this.dateValidationEndOriginal = this.dateValidationEnd;
        this.dateValidationEnd = this.getMomentDate(this.dateValidationEnd);
        this.nextAvailableDate = this.getMomentDate(this.nextAvailableDate);
        this.routeBlocked = this.getRouteBlocked();
        this.labelBlocked = this.getLabelBlocked();
        this.nextValidityDate = this.getNextValidityDate();
        this.imageReward = imageClass.getImage(this.imageReward, this.cardSubTypeAcronym);
        this.hasGeneratedCode = this.generatedCodes && this.generatedCodes.length > 0;
        this.value = FormatDecimalValue.convertDecimal(this.value);
        this.isDisabled = this.status === "FINESHED" && this.cardSubTypeAcronym === "DRAW";
        this.closingDate = this.getMomentDate(this.closingDate, "DD MMM YYYY");
        this.validityCodeDate = this.getMomentDate(this.validityCodeDate, "DD MMM YYYY");
        this.filterSubtype = [
            "DAY_TO_DAY",
            "SPORT_OUTDOORS",
            "FAMILY",
            "WELLBEING_HEALTH",
            "RESTAURANTS",
            "ACCOMMODATION",
            "PRODUCTS_SERVICES",
            "DRAW",
        ];
        this.usages = this.usages !== "0" ? this.usages : null;
    }

    private initCard() {
        switch (this.cardSubTypeAcronym) {
            case "INVOICE_DISCOUNT":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.InvoiceDiscount";
                this.isDiscount = true;
                this.icon = "";
                break;
            case "PARTNER":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.PartnerDiscount";
                this.isDiscount = true;
                this.icon = "icon-edponline-Bop-226";
                break;
            case "EXPERIENCE":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.ExclusiveExperience";
                this.isExclusiveExperience = true;
                this.showBadge = true;
                this.icon = "";
                break;
            case "DRAW":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.DrawInProgress";
                this.isGiveAway = true;
                this.icon = "icon-edponline-EDP_Online_02";
                break;
            case "INDIVIDUAL_CHALLENGE":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.IndividualChallenge";
                this.isIndividualChallenge = true;
                this.icon = "";
                break;
            case "COLLECTIVE_CHALLENGE":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.CollectiveChallenge";
                this.isCollectiveChallenge = true;
                this.icon = "";
                break;
            case "DONATION":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.Donative";
                this.isDonative = true;
                this.icon = "";
                break;
            case "PRODUCT":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.ProductDiscount";
                this.isProductDiscount = true;
                break;
            case "CONTENT":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.Content";
                this.isContent = true;
                this.icon = "";
                break;
            case "DAY_TO_DAY":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.DAY_TO_DAY";
                this.icon = "icon-edponline-Bop-112";
                break;
            case "SPORT_OUTDOORS":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.SPORT_OUTDOORS";
                this.icon = "icon-edponline-Bop-206";
                break;
            case "FAMILY":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.FAMILY";
                this.icon = "icon-edponline-Bop-095";
                break;
            case "WELLBEING_HEALTH":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.WELLBEING_HEALTH";
                this.icon = "icon-edponline-Bop-184";
                break;
            case "RESTAURANTS":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.RESTAURANTS";
                this.icon = "icon-edponline-Bop-127";
                break;
            case "ACCOMMODATION":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.ACCOMMODATION";
                this.icon = "icon-edponline-Bop-019";
                break;
            case "PRODUCTS_SERVICES":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.PRODUCTS_SERVICES";
                this.icon = "icon-edponline-Bop-001";
                break;
            case "OTHERS":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.OTHERS";
                this.icon = "icon-edponline-Bop-226";
                break;
            case "ALLPACKSUBTYPES":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.ALLPACKSUBTYPES";
                this.icon = "";
                break;
            case "QUIZZ":
                this.cardTitle = "pt.edp.edponline.benefits.filters.cardSubtype.Quizz";
                this.isQuizz = true;
                break;
            default:
                this.cardTitle = this.name;
                break;
        }
        this.status = this.status === "BLOCKED (+1)" ? "BLOCKED" : this.status;
        this.isCampaignPartner = this.getTypeValue() === 7;
    }

    private getTypeValue() {
        return this.type && this.type.value;
    }

    public getColorCard(): string {
        switch (this.cardSubTypeAcronym) {
            case "DRAW":
                // $edp-seaweed-green-02
                return "#43767D";

            default:
                // $edp-cobalt-blue-02
                return "#4759D0";
        }
    }

    public getSecundaryColorCard(): string {
        switch (this.cardSubTypeAcronym) {
            case "DRAW":
                // $edp-seaweed-green-03
                return "#648E94";

            default:
                // $edp-cobalt-blue-03
                return "#7D8ADE";
        }
    }

    private getStatusName() {
        return cardStatus.find(st => st.value === this.status).name;
    }

    private getNextValidityDate() {
        if (this.appliedRules && this.appliedRules.length > 0) {
            return this.getMomentDate(this.appliedRules[0].nextValidityDate);
        } else if (this.nextValidityDate) {
            return this.getMomentDate(this.nextValidityDate);
        }
        return null;
    }

    /* public getImage(img: string, cardSubType?: string) {
        if (!img) {
            return null;
        } else if (img.indexOf("resource") >= 0) {
            return `${environment.appUrl.PT}${img}`;
        } else {
            if (cardSubType === "INDIVIDUAL_CHALLENGE") {
                return `/client/user/resource/${img}`;
            }
            return `${environment.appUrl.PT}/resource/image/${img}`;
        }
    } */

    private getTermsAndConditions(terms: string) {
        if (!terms || terms.indexOf("resource") >= 0) {
            return `${environment.appUrl.PT}${terms}`;
        } else {
            return `${environment.appUrl.PT}/resource/image/${terms}`;
        }
    }

    getCookie(name) {
        function escape(s) {
            return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, "\\$1");
        }
        const match = document.cookie.match(RegExp("(?:^|;\\s*)" + escape(name) + "=([^;]*)"));
        return match ? match[1] : "pt";
    }

    private getMomentDate(_date, format = "DD MMM YYYY") {
        return _date
            ? moment(_date)
                  .locale(this.getCookie("user_language"))
                  .format(format)
            : null;
    }

    private getRouteBlocked() {
        if (this.status === "BLOCKED") {
            switch (this.blockedType) {
                case 0:
                    return "/novo-plano";
                case 1:
                    return "/planeta-zero/inscricao";
                case 2:
                case 3:
                case 4:
                    return "/planeta-zero/seu-nivel";
            }
        }
    }

    private getLabelBlocked() {
        if (this.status === "BLOCKED") {
            switch (this.blockedType) {
                case -1:
                    return "pt.edp.edponline.locked.type.-1";
                case 0:
                    return "pt.edp.edponline.locked.type.0";
                case 1:
                    return "pt.edp.edponline.locked.type.1";
                case 2:
                    return "pt.edp.edponline.benefits.partner.state.locked.2";
                case 3:
                    return "pt.edp.edponline.benefits.partner.state.locked.3";
                case 4:
                    return "pt.edp.edponline.benefits.partner.state.locked.4";
                case 10:
                    return "pt.edp.edponline.locked.type.UNAVAILABLE";
                case 11:
                    return "pt.edp.edponline.locked.type.WAITING_LIST";
                case 12:
                    return "pt.edp.edponline.locked.type.CLOSED";
            }
        }
    }
}

export class BenefitsFactory {
    static create(card: ICard): Card {
        return new Card(
            card.filterSubtype,
            card.partnerTypeAcronym,
            card.isDisabled,
            card.icon,
            card.btnParticipate,
            card.location,
            card.cardTitle,
            card.id,
            card.name,
            card.description,
            card.formtypeAcronym,
            card.image,
            card.imageMobile,
            card.value,
            card.dateValidationStart,
            card.dateValidationEnd,
            card.dateValidationEndOriginal,
            card.participationDate,
            card.imageId,
            card.type,
            card.order,
            card.externalLink,
            card.campaign,
            card.cardTypeId,
            card.cardSubtypeId,
            card.cardSubtype,
            card.dashboardCoordinateId,
            card.status,
            card.codesAvailable,
            card.nextAvailableDate,
            card.level,
            card.seats as Seats,
            card.modalContent,
            card.blockedType,
            card.generatedCode,
            card.routeBlocked,
            card.labelBlocked,
            card.eventName,
            card.cardSubTypeAcronym,
            card.nextValidityDate,
            card.termsAndConditions,
            card.appliedRules,
            card.categoryId,
            card.partners,
            card.options ? card.options : [],
            card.points,
            card.generatedCodes,
            card.resultsLink,
            card.isRegisted,
            card.zNumbers,
            card.imageReward,
            card.quizzScript,
            card.answeredQuizz,
            card.boost,
            card.highlight,
            card.activityTitle,
            card.activityDescription,
            card.partnerDescription,
            card.partnerLink,
            card.howToUseCode,
            card.earnedZs,
            card.showBadge,
            card.inMacro,
            card.hasGeneratedCode,
            card.thumbnailLabel,
            card.thumbnailImage,
            card.partnerAddress,
            card.partnerContact,
            card.winner,
            card.winningAnnouncementDate,
            card.psubtypeAcronym,
            card.mechanicType,
            card.districts,
            card.partnerStepByStepUsage,
            card.closingDate,
            card.validityCodeDate,
            card.usages
        );
    }

    static createGeneratedCode(card: ICard, generatedCode: GeneratedCodes): Card {
        const name = "user_language";
        let locale = "pt";

        function escape(s) {
            return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, "\\$1");
        }
        const match = document.cookie.match(RegExp("(?:^|;\\s*)" + escape(name) + "=([^;]*)"));
        locale = match ? match[1] : (locale = "pt");

        const generatedCodeCard = this.create(card);
        if (generatedCode) {
            generatedCodeCard.generatedCode = generatedCode;
            generatedCodeCard.generatedCode.validationDate = moment(generatedCodeCard.generatedCode.validationDate)
                .locale(locale)
                .format("DD MMM YYYY");
        }
        return generatedCodeCard;
    }
}

export interface IFilter {
    id: number;
    name: string;
    urlName: string;
    allOptionsLabel: string;
    allSelected: boolean;
    options: FilterOption[];
    mobileCollapsable: boolean;
    isMobileFilter: boolean;
    hasOptionSelected: boolean;
    clicked: boolean;
}

export interface IBenefitsUserDetails {
    pn: string;
    nif: string;
    onboarding: boolean;
    pageCounter: string;
    start: string;
    renewal: string;
}

export interface FilterOption {
    id: number;
    name: string;
    value: string;
    selected: boolean;
}

export class Filter {
    constructor(
        public id: number,
        public name: string,
        public urlName: string,
        public allOptionsLabel: string,
        public allSelected: boolean,
        public options: FilterOption[],
        public mobileCollapsable: boolean = false,
        public isMobileFilter: boolean = false,
        public hasOptionSelected: boolean = false,
        public clicked: boolean = false
    ) {}
}

export class FiltersFactory {
    static generateFilters(cardTypes: CardType[]) {
        const cardTypeOptions: FilterOption[] = [];
        const cardSubtypeOptions: FilterOption[] = [];
        const cardStatusOptions: FilterOption[] = cardStatus;

        // Exclude Cards "Desafio Individual", "Desafio Colectivo" and "Conteúdo"
        const excludedCardSubtypes = ["INDIVIDUAL_CHALLENGE", "COLLECTIVE_CHALLENGE", "CONTENT"];
        cardTypes.forEach(type => {
            if (type.parentId === null) {
                cardTypeOptions.push({
                    id: type.id,
                    name: type.name,
                    value: type.code,
                    selected: false,
                });
            } else if (!excludedCardSubtypes.includes(type.code)) {
                if (!cardSubtypeOptions.find(option => option.value === type.code)) {
                    cardSubtypeOptions.push({
                        id: type.id,
                        name: type.name,
                        value: type.code,
                        selected: false,
                    });
                }
            }
        });

        const ftrCardTypes = new Filter(
            1,
            "pt.edp.edponline.benefits.filters.cardType",
            "Area",
            "pt.edp.edponline.benefits.filters.allCardTypes",
            true,
            cardTypeOptions,
            true
        );
        const ftrCardSubtypes = new Filter(
            2,
            "pt.edp.edponline.benefits.filters.cardSubtype",
            "Type",
            "pt.edp.edponline.benefits.filters.allCardSubtypes",
            true,
            cardSubtypeOptions,
            false
        );
        const ftrCardStatus = new Filter(
            3,
            "pt.edp.edponline.benefits.filters.status",
            "Status",
            "pt.edp.edponline.benefits.filters.allStatus",
            true,
            cardStatusOptions,
            true
        );

        const mobileFilter = new Filter(
            0,
            "pt.edp.edponline.benefits.filters.moreFilters",
            "More",
            "",
            true,
            [...cardSubtypeOptions, ...cardTypeOptions],
            false,
            true
        );

        return [ftrCardSubtypes, ftrCardStatus, ftrCardTypes, mobileFilter];
    }
}

const cardStatus = [
    {
        id: 1,
        name: "pt.edp.edponline.benefits.card.status.available",
        value: "AVAILABLE",
        selected: false,
    },
    {
        id: 2,
        name: "pt.edp.edponline.benefits.card.status.blocked",
        value: "BLOCKED",
        selected: false,
    },
    {
        id: 3,
        name: "pt.edp.edponline.benefits.card.status.used",
        value: "USED",
        selected: false,
    },
];

export interface Section {
    id: number;
    title: string;
    cards: Card[] | ICard[];
    promotion: boolean;
    challenge: SectionChallenge;
}

export interface SectionChallenge {
    description: string;
    id: number;
    image: string;
    numberOfZ: number;
    prize: string;
    title: string;
    validityDate: string;
    completed: number;
    modalShown: number;
    successImage: string;
    type: string;
    events?: ChallengeEvent[];
    userMacroChallengeId?: string;
    totalCardsChallengeAchieved?: any;
    totalCards?: any;
}

export interface ChallengeEvent {
    id: number;
    name: string;
    local: string;
    startDate: string;
}

export interface IBenefitsCounterItem {
    codes: Array<string>;
    name: string;
    date_validation: Date;
    partner_id: string;
}

export interface IBenefitsCounter {
    full_value: number;
    used_value: number;
    benefits: Array<IBenefitsCounterItem>;
}

export interface ExclusiveBenefitsCardI {
    thumbnailImage: string;
    thumbnailLabel: string;
}

export class ExclusiveBenefitsCard {
    constructor(public thumbnailImage: string, public thumbnailLabel: string) {
        const image = new GetImage();
        this.thumbnailImage = image.getImage(this.thumbnailImage);
    }
}

export class ExclusiveBenefitsCardFactory {
    static create(exclusiveBenefitsCard: any): ExclusiveBenefitsCard {
        return new ExclusiveBenefitsCard(exclusiveBenefitsCard.thumbnailImage, exclusiveBenefitsCard.thumbnailLabel);
    }
}

export class GetImage {
    constructor() {}

    public getImage(img: string, cardSubType?: string) {
        if (!img) {
            return null;
        } else if (img.indexOf("resource") >= 0) {
            return `${environment.appUrl.PT}${img}`;
        } else {
            if (cardSubType === "INDIVIDUAL_CHALLENGE") {
                return `/client/user/resource/${img}`;
            }
            return `${environment.appUrl.PT}/resource/image/${img}`;
        }
    }
}

export interface BenefitsDistricts {
    id: string;
    title: string;
    acronym: string;
    active?: boolean;
}

export interface AvailableOfferFilter {
    offerAcronym?: string;
    districtAcronym?: string;
}
